/**
 * Operator labels for owner-scoped lists (KAN-627).
 *
 * The owner of the cabinet sees rows of several operators (campaigns, etc.).
 * Rows carry only the operator's user id, so the label (full name, else email)
 * is resolved from the org users list. The list is fetched only when the rows
 * really belong to more than one operator: a single-operator view shows no
 * operator column and costs no extra request.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/services/api/users';

export interface OperatorLabels {
  /** True when the rows belong to more than one operator. */
  showOperator: boolean;
  /** Label for an operator id: full name, else email, else empty string. */
  labelFor: (id: string | null | undefined) => string;
}

export function useOperatorLabels(
  ids: Array<string | null | undefined>
): OperatorLabels {
  const distinct = useMemo(
    () => new Set(ids.filter((id): id is string => Boolean(id))),
    [ids]
  );
  const showOperator = distinct.size > 1;

  const { data } = useQuery({
    queryKey: ['users', 'operator-labels'],
    queryFn: () => usersApi.list({ page_size: 200 }),
    enabled: showOperator,
    staleTime: 5 * 60 * 1000,
  });

  const labels = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of data?.results ?? []) {
      map.set(String(u.id), u.full_name?.trim() || u.email);
    }
    return map;
  }, [data]);

  const labelFor = useMemo(
    () => (id: string | null | undefined) => (id ? labels.get(String(id)) ?? '' : ''),
    [labels]
  );

  return { showOperator, labelFor };
}
